// /reports — Reports Dashboard (PAGES.md §3.39)
//
// Central hub: category tabs + grid of report tiles. Role, vertical, feature-flag
// and tax-regime gating comes from the shared report registry — the same source the
// sidebar links and the route guard read — so a tile is offered exactly when the
// route behind it will open (issue #253).

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageContainer, ErrorBoundary, Tabs, Badge, EmptyState } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { ROLE_LEVELS, REPORTS, canAccessReport } from '@/features/reports';
import type { ReportCategory, ReportDefinition, ReportTileMeta, ReportTone } from '@/features/reports';

type Category = ReportCategory;

/** A report that has tile presentation — the only kind this page renders. */
type Tile = ReportDefinition & { tile: ReportTileMeta };

const CATEGORIES: { id: Category; label: string }[] = [
  { id: 'sales', label: 'Sales' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'financial', label: 'Financial' },
  { id: 'customer', label: 'Customer' },
  { id: 'staff', label: 'Staff' },
  { id: 'restaurant', label: 'Restaurant' },
];

const TONE_CLASSES: Record<ReportTone, { card: string; ring: string; iconBg: string; chip: string }> = {
  brand:   { card: 'hover:shadow-[0_20px_40px_-20px_rgba(37,99,235,0.55)]',   ring: 'hover:ring-brand-200 dark:hover:ring-brand-700',   iconBg: 'bg-gradient-to-br from-brand-500 to-indigo-600',   chip: 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200' },
  indigo:  { card: 'hover:shadow-[0_20px_40px_-20px_rgba(99,102,241,0.55)]',  ring: 'hover:ring-indigo-200 dark:hover:ring-indigo-700', iconBg: 'bg-gradient-to-br from-indigo-500 to-violet-600',  chip: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200' },
  violet:  { card: 'hover:shadow-[0_20px_40px_-20px_rgba(168,85,247,0.55)]',  ring: 'hover:ring-violet-200 dark:hover:ring-violet-700', iconBg: 'bg-gradient-to-br from-violet-500 to-fuchsia-600', chip: 'bg-violet-50 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200' },
  pink:    { card: 'hover:shadow-[0_20px_40px_-20px_rgba(236,72,153,0.55)]',  ring: 'hover:ring-pink-200 dark:hover:ring-pink-700',     iconBg: 'bg-gradient-to-br from-pink-500 to-rose-600',      chip: 'bg-pink-50 text-pink-700 dark:bg-pink-900/40 dark:text-pink-200' },
  teal:    { card: 'hover:shadow-[0_20px_40px_-20px_rgba(13,148,136,0.55)]',  ring: 'hover:ring-teal-200 dark:hover:ring-teal-700',     iconBg: 'bg-gradient-to-br from-teal-500 to-success-600',   chip: 'bg-teal-50 text-teal-700 dark:bg-teal-900/40 dark:text-teal-200' },
  success: { card: 'hover:shadow-[0_20px_40px_-20px_rgba(16,185,129,0.55)]',  ring: 'hover:ring-success-200 dark:hover:ring-success-700', iconBg: 'bg-gradient-to-br from-success-500 to-teal-600', chip: 'bg-success-50 text-success-700 dark:bg-success-900/40 dark:text-success-200' },
  warning: { card: 'hover:shadow-[0_20px_40px_-20px_rgba(245,158,11,0.55)]',  ring: 'hover:ring-warning-200 dark:hover:ring-warning-700', iconBg: 'bg-gradient-to-br from-warning-500 to-orange-600', chip: 'bg-warning-50 text-warning-700 dark:bg-warning-900/40 dark:text-warning-200' },
};

function TileIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-6h13M9 11V5H4v6m0 0v6a2 2 0 002 2h15M9 11h13" />
    </svg>
  );
}

function NoReportsIcon() {
  return (
    <svg className="h-16 w-16 text-neutral-300 dark:text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function ReportsDashboardContent() {
  const profile = useAuthStore((s) => s.profile);
  const flags = useFeatureFlag();
  const tax = useTaxRegime();
  const [activeCategory, setActiveCategory] = useState<Category>('sales');

  const role = profile?.role ?? '';
  const level = ROLE_LEVELS[role] ?? 0;
  const isRestaurant = profile?.tenant?.business_type === 'RESTAURANT';
  const isPlatform = level >= 60;

  const visibleTiles: Tile[] = useMemo(() => {
    // The registry — shared with the sidebar links and the route guard — owns the
    // role / vertical / feature-flag / tax-regime rules. This page only decides how
    // an accessible report is *presented*.
    const ctx = {
      roleLevel: level,
      businessType: profile?.tenant?.business_type,
      flags,
      tax,
    };
    return REPORTS.filter((r): r is Tile => Boolean(r.tile) && canAccessReport(r, ctx)).map((t) => {
      // The GST report exists for every regime but its vocabulary is regime
      // driven: GCC sees a single VAT line (no CGST/SGST/IGST). INR keeps the
      // exact legacy copy ('GST Report' / 'CGST / SGST / IGST by slab …').
      if (t.id === 'gst') {
        return {
          ...t,
          label: `${tax.taxName} Report`,
          tile: {
            ...t.tile,
            description: tax.splitLabels
              ? 'CGST / SGST / IGST by slab for the filing period.'
              : `${tax.taxName} by slab for the filing period.`,
          },
        };
      }
      return t;
    });
  }, [level, profile?.tenant?.business_type, flags, tax]);

  const visibleCategories = CATEGORIES.filter((c) => {
    // Hide Restaurant category entirely for non-restaurant, non-platform users
    if (c.id === 'restaurant' && !isRestaurant && !isPlatform) return false;
    return visibleTiles.some((t) => t.tile.category === c.id);
  });

  // `activeCategory` is the user's *choice*; it is not necessarily valid for this
  // role / vertical / flag combination (the initial 'sales' may be gated away, or the
  // selected tab may disappear after a role change). Fall back to the first visible
  // category by deriving it here rather than correcting the state — a setter in the
  // render body is a render-phase update, and an effect would commit one frame with
  // the stale tab first. Deriving keeps tabs and grid in agreement on the first pass.
  const effectiveCategory = visibleCategories.some((c) => c.id === activeCategory)
    ? activeCategory
    : (visibleCategories[0]?.id ?? activeCategory);

  const tilesByCategory = useMemo(() => visibleTiles.filter((t) => t.tile.category === effectiveCategory), [visibleTiles, effectiveCategory]);

  // A role below every tile's minRoleLevel (a CASHIER clears the route guard but no tile
  // gate) gates away *all* tiles, which also leaves zero categories. Without a branch here
  // the page renders an empty tab strip over an empty grid — a blank area under the hero
  // with nothing explaining it. Tell the user why instead, the way ReportGuard does when
  // one of these reports is opened directly.
  const hasReports = visibleTiles.length > 0;

  return (
    <div className="animate-fade-in">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-600 via-indigo-600 to-violet-600 p-7 sm:p-8 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(99,102,241,0.6)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-60 w-60 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-black/10 blur-3xl" />
        <div className="relative">
          <Badge variant="info" size="sm">
            <span className="text-[10px] tracking-wider font-bold">REPORTS CENTER</span>
          </Badge>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">Business Intelligence</h1>
          <p className="mt-2 text-white/85 max-w-2xl">
            Every metric that matters — from {tax.taxName} filings to kitchen throughput — in one place. All reports are
            role-aware, location-filtered, and ready to share on WhatsApp or Email.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold">
              {visibleTiles.length} reports available
            </span>
            <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold">
              Role: {role || '—'}
            </span>
            <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold">
              {isRestaurant ? 'Restaurant' : 'Retail'} vertical
            </span>
          </div>
        </div>
      </div>

      {!hasReports ? (
        <div className="rounded-2xl bg-gradient-to-br from-neutral-50 to-white dark:from-neutral-800 dark:to-neutral-900 ring-1 ring-neutral-100 dark:ring-neutral-700 shadow-md p-10 animate-fade-in">
          <EmptyState
            variant="page"
            icon={<NoReportsIcon />}
            title="You don't have access to any reports"
            description={`Reports in this hub need Manager access or above, and your role${role ? ` (${role})` : ''} is below that. Contact your administrator if you need access.`}
          />
        </div>
      ) : (
        <>
          {/* Category tabs */}
          <div className="mb-6">
            <Tabs
              items={visibleCategories.map((c) => ({
                id: c.id,
                label: c.label,
                badge: visibleTiles.filter((t) => t.tile.category === c.id).length,
              }))}
              activeId={effectiveCategory}
              onChange={(id) => setActiveCategory(id as Category)}
              variant="pill"
              scrollable
            />
          </div>

          {/* Report tiles grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
            {tilesByCategory.map((tile, idx) => {
              const tone = TONE_CLASSES[tile.tile.tone];
              return (
                <Link
                  key={tile.id}
                  to={tile.path}
                  className={`group relative overflow-hidden rounded-2xl bg-white dark:bg-neutral-800 ring-1 ring-neutral-100 dark:ring-neutral-700 shadow-md transition-all duration-200 ${tone.card} ${tone.ring} p-5 animate-slide-up`}
                  style={{ animationDelay: `${idx * 40}ms` }}
                >
                  <div className="absolute top-0 right-0 h-24 w-24 -translate-y-8 translate-x-8 rounded-full bg-gradient-to-br from-brand-100 to-violet-100 dark:from-brand-900/30 dark:to-violet-900/30 opacity-60" />
                  <div className="relative flex items-start justify-between gap-3">
                    <div className={`w-12 h-12 rounded-xl ${tone.iconBg} flex items-center justify-center text-white shadow-lg`}>
                      <TileIcon />
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold tracking-wider uppercase ${tone.chip}`}>
                      {tile.tile.category}
                    </span>
                  </div>
                  <h3 className="relative mt-4 text-base font-bold text-neutral-900 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-300 transition-colors">
                    {tile.label}
                  </h3>
                  <p className="relative mt-1 text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">{tile.tile.description}</p>
                  {tile.minRoleLevel >= 50 ? (
                    <p className="relative mt-3 text-[10px] font-bold uppercase tracking-wider text-warning-600 dark:text-warning-400">
                      Owner access
                    </p>
                  ) : null}
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function ReportsDashboardPage() {
  return (
    <PageContainer title="Reports" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports' }]}>
      <ErrorBoundary resetKey="reports-dashboard">
        <ReportsDashboardContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
